/**
 * sessionStore.ts — Zustand session state machine (Pattern 6 from 02-RESEARCH.md).
 *
 * State machine with explicit transition validation.
 * All UI element visibility and enabled states derive from `mode`.
 *
 * Modes:
 *   idle        — no active session
 *   connecting  — WebSocket opening (or reconnecting after pause)
 *   ai_speaking — AI audio/text streaming to browser
 *   gary_speaking — Gary's turn: mic active (voice) or text input enabled (text)
 *   processing  — final transcript sent; awaiting Claude response
 *   paused      — Page Visibility hidden; WS will reconnect on return
 *   error       — recoverable or unrecoverable error
 */

import { create } from 'zustand';
import { newSessionId } from '../lib/idGenerator';

// ── Types ─────────────────────────────────────────────────────────────────────

export type SessionMode =
  | 'idle'
  | 'connecting'
  | 'ai_speaking'
  | 'gary_speaking'
  | 'processing'
  | 'paused'
  | 'error';

export interface Turn {
  id: string;          // ULID
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  /** Blob URLs for in-chat uploaded images — in-memory only, not persisted */
  mediaPreviews?: string[];
  /** Filename of an attached document — displayed as a chip in the transcript */
  documentFilename?: string;
}

export interface SessionError {
  code: string;
  message: string;
}

// ── State machine transition table ───────────────────────────────────────────
// Only these transitions are valid. setMode() enforces this table.
// Any state → paused (Page Visibility) and any state → idle (end session)
// are handled via explicit pause() and reset() actions.

const VALID_TRANSITIONS: Record<SessionMode, SessionMode[]> = {
  idle:          ['connecting'],
  connecting:    ['connecting', 'ai_speaking', 'gary_speaking', 'paused', 'error', 'idle'],
  ai_speaking:   ['gary_speaking', 'processing', 'paused', 'error', 'idle'],
  gary_speaking: ['processing', 'paused', 'error', 'idle'],
  processing:    ['ai_speaking', 'gary_speaking', 'paused', 'error', 'idle'],
  paused:        ['connecting', 'idle', 'error'],
  error:         ['connecting', 'idle'],
};

// ── Store interface ───────────────────────────────────────────────────────────

export interface SessionStore {
  // State
  mode: SessionMode;
  sessionId: string | null;
  currentMode: 'voice' | 'text';
  turns: Turn[];
  streamingAiText: string;
  partialTranscript: string;
  error: SessionError | null;

  // Actions
  setMode: (mode: SessionMode) => void;
  setSessionId: (id: string, mode: 'voice' | 'text') => void;
  addTurn: (turn: Turn) => void;
  appendAiDelta: (text: string) => void;
  finalizeAiTurn: (turnId: string) => void;
  setPartialTranscript: (text: string) => void;
  clearPartialTranscript: () => void;
  pause: () => void;
  resume: () => void;
  setError: (error: SessionError) => void;
  reset: () => void;
}

// ── Initial state ─────────────────────────────────────────────────────────────

const initialState = {
  mode: 'idle' as SessionMode,
  sessionId: null as string | null,
  currentMode: 'text' as 'voice' | 'text',
  turns: [] as Turn[],
  streamingAiText: '',
  partialTranscript: '',
  error: null as SessionError | null,
};

// ── Store ─────────────────────────────────────────────────────────────────────

export const useSessionStore = create<SessionStore>((set, get) => ({
  ...initialState,

  setMode(newMode: SessionMode) {
    const current = get().mode;
    const allowed = VALID_TRANSITIONS[current];
    if (!allowed.includes(newMode)) {
      console.warn(
        `[sessionStore] Invalid transition: ${current} → ${newMode}. ` +
        `Allowed from ${current}: [${allowed.join(', ')}]`
      );
      return;
    }
    set({ mode: newMode });
  },

  setSessionId(id: string, mode: 'voice' | 'text') {
    set({ sessionId: id, currentMode: mode });
  },

  addTurn(turn: Turn) {
    set((state) => ({ turns: [...state.turns, turn] }));
  },

  appendAiDelta(text: string) {
    set((state) => ({ streamingAiText: state.streamingAiText + text }));
  },

  finalizeAiTurn(turnId: string) {
    const { streamingAiText } = get();
    if (streamingAiText.length === 0) return;
    const turn: Turn = {
      id: turnId,
      role: 'assistant',
      content: streamingAiText,
      createdAt: new Date().toISOString(),
    };
    set((state) => ({
      turns: [...state.turns, turn],
      streamingAiText: '',
    }));
  },

  setPartialTranscript(text: string) {
    set({ partialTranscript: text });
  },

  clearPartialTranscript() {
    set({ partialTranscript: '' });
  },

  pause() {
    set({ mode: 'paused', partialTranscript: '' });
  },

  resume() {
    // Sets to 'connecting' so the socket wrapper re-enables and reconnects
    set({ mode: 'connecting' });
  },

  setError(error: SessionError) {
    set({ mode: 'error', error });
  },

  reset() {
    set({ ...initialState, sessionId: null });
  },
}));

// ── Convenience selector ──────────────────────────────────────────────────────

/** Returns a new ULID suitable for a turn ID. Re-exported here for hook convenience. */
export { newSessionId as newTurnId };
